import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building2, X, Search, Loader2 } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function CompaniesSelector(props) {
  var selectedCompanies = props.selectedCompanies || [];
  var onToggleCompany = props.onToggleCompany;

  var searchState = useState('');
  var searchTerm = searchState[0];
  var setSearchTerm = searchState[1];

  var resultsState = useState([]);
  var searchResults = resultsState[0];
  var setSearchResults = resultsState[1];

  var loadingState = useState(false);
  var searching = loadingState[0];
  var setSearching = loadingState[1];

  var allCompaniesState = useState([]);
  var allCompanies = allCompaniesState[0];
  var setAllCompanies = allCompaniesState[1];

  var showDropdownState = useState(false);
  var showDropdown = showDropdownState[0];
  var setShowDropdown = showDropdownState[1];

  // Fetch all companies on mount for initial display
  useEffect(function() {
    var token = localStorage.getItem('token');
    axios.get(API + '/masters/companies', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setAllCompanies(res.data.companies || []);
    }).catch(function() {});
  }, []);

  // Typeahead search (min 3 chars)
  useEffect(function() {
    if (searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/masters/companies/search?q=' + encodeURIComponent(searchTerm), {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setSearchResults(res.data.companies || []);
      setSearching(false);
    }).catch(function() {
      setSearching(false);
    });
  }, [searchTerm]);

  // Companies to show: search results if searching, else all active
  var displayCompanies = searchTerm.length >= 3
    ? searchResults
    : allCompanies.filter(function(c) { return c.is_active !== false; });

  return React.createElement('div', { className: 'space-y-3', 'data-testid': 'companies-selector' },
    React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
      React.createElement(Building2, { size: 16, className: 'text-purple-600' }),
      React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Companies Handled')
    ),

    // Selected companies tags
    selectedCompanies.length > 0 && React.createElement('div', { className: 'flex flex-wrap gap-1.5' },
      selectedCompanies.map(function(companyName) {
        return React.createElement(Badge, {
          key: companyName,
          className: 'gap-1 bg-purple-100 text-purple-800 border-purple-200 cursor-pointer hover:bg-purple-200',
          onClick: function() { onToggleCompany(companyName); },
          'data-testid': 'selected-company-' + companyName
        },
          companyName,
          React.createElement(X, { size: 12 })
        );
      })
    ),

    // Search input
    React.createElement('div', { className: 'relative' },
      React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 text-slate-400', size: 14 }),
      React.createElement(Input, {
        value: searchTerm,
        onChange: function(e) {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        },
        onFocus: function() { setShowDropdown(true); },
        placeholder: 'Type 3+ letters to search companies...',
        className: 'pl-8 h-9 text-sm',
        'data-testid': 'company-search-input'
      }),
      searching && React.createElement(Loader2, {
        className: 'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400'
      })
    ),

    // Dropdown results
    showDropdown && React.createElement('div', {
      className: 'border rounded-lg max-h-40 overflow-y-auto bg-white shadow-sm'
    },
      displayCompanies.length > 0
        ? displayCompanies.map(function(company) {
            var isSelected = selectedCompanies.includes(company.name);
            return React.createElement('div', {
              key: company.id || company.name,
              className: 'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ' + (isSelected ? 'bg-purple-50' : ''),
              onClick: function() { onToggleCompany(company.name); },
              'data-testid': 'company-option-' + company.name
            },
              React.createElement('input', {
                type: 'checkbox',
                checked: isSelected,
                readOnly: true,
                className: 'rounded border-slate-300 text-purple-600'
              }),
              React.createElement('span', { className: 'text-sm' }, company.name)
            );
          })
        : React.createElement('div', { className: 'p-3 text-center text-sm text-slate-400' },
            searchTerm.length >= 3
              ? 'No company found \u2013 please add in Admin Settings'
              : 'Type 3+ letters to search'
          )
    )
  );
}

export default CompaniesSelector;
